import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import {
  demoEmail,
  matchesSearchEndpoint,
  type DemoFixtureIdentity,
} from 'tio-employee/demo/types';
import { buildDemoEmployeeCatchAllDoc, toRawDoc } from 'tio-employee/demo/identity-fixtures';
import type { DemoSessionSelectionSnapshot } from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Admin-table fixtures shared across demo products, with session-derived identity and consistent colleague personas.
 * Collections need meta.page.total; eligibility jobs must be terminal to avoid polling unfixtured records.
 * Eligibility cards require companySetting.eligibilityDefault. Reports need published partner_admin scope,
 * an allowed product type, no archive flag, and global visibility for the nonnumeric demo company ID.
 */

const PAGE_META = (total: number) => ({ page: { total, offset: 0, limit: 50 } });

const employerSlug = (employerName: string) =>
  employerName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Columns: payrollId, first/last name, email, participationStatus, plan.name,
// eligibilityStartDate, createdAt, statistics.user.total_accounts/total_loans.
// The first row is the session user; the EMP-1001/1042/1088 rows match the
// TAS approval-queue applicants so the two surfaces tell one story.
interface DirectoryRow {
  id: string;
  payrollId: string;
  firstName: string;
  lastName: string;
  eligibilityStartDate: string;
  createdAt: string;
  accounts: number;
  loans: number;
  /** Registered implies enrolled, so the enrollment rail's buckets nest correctly. */
  isEnrolled: boolean;
  isRegistered: boolean;
  /** Values must come from PARTNER_VISIBLE_ROLE_KEYS or the roles rail drops them. */
  jurisdictions?: string[];
}

const DIRECTORY_ROWS: Omit<DirectoryRow, 'firstName' | 'lastName'>[] = [
  {
    id: 'demo-dir-employee-1',
    payrollId: 'EMP-1000',
    eligibilityStartDate: '2022-02-01',
    createdAt: '2022-01-15T09:00:00Z',
    accounts: 1,
    loans: 2,
    isEnrolled: true,
    isRegistered: true,
    jurisdictions: ['EMPLOYEE_ADMIN'],
  },
];

const COLLEAGUE_ROWS: DirectoryRow[] = [
  {
    id: 'demo-dir-employee-2',
    payrollId: 'EMP-1001',
    firstName: 'Jordan',
    lastName: 'Lee',
    eligibilityStartDate: '2023-04-01',
    createdAt: '2023-03-12T14:30:00Z',
    accounts: 1,
    loans: 3,
    isEnrolled: true,
    isRegistered: true,
    jurisdictions: ['SUPERVISOR'],
  },
  {
    id: 'demo-dir-employee-3',
    payrollId: 'EMP-1042',
    firstName: 'Priya',
    lastName: 'Natarajan',
    eligibilityStartDate: '2024-01-15',
    createdAt: '2023-12-20T11:15:00Z',
    accounts: 1,
    loans: 1,
    isEnrolled: true,
    isRegistered: false,
    jurisdictions: ['TAS_APPROVER'],
  },
  {
    id: 'demo-dir-employee-4',
    payrollId: 'EMP-1088',
    firstName: 'Marcus',
    lastName: 'Brown',
    eligibilityStartDate: '2024-09-01',
    createdAt: '2024-08-05T16:45:00Z',
    accounts: 2,
    loans: 4,
    isEnrolled: false,
    isRegistered: false,
  },
  {
    id: 'demo-dir-employee-5',
    payrollId: 'EMP-1112',
    firstName: 'Sofia',
    lastName: 'Alvarez',
    eligibilityStartDate: '2025-06-01',
    createdAt: '2025-05-18T08:20:00Z',
    accounts: 1,
    loans: 2,
    isEnrolled: true,
    isRegistered: true,
  },
];

/** `matchedQueries` is omitted: present, it renders match chips on an unsearched page. */
function peopleHit(row: DirectoryRow) {
  return {
    _index: 'people',
    _id: row.id,
    _score: 1,
    _source: { personIdKeyword: row.id },
    innerHits: {
      employments: {
        hits: {
          hits: [
            {
              _source: {
                employeeIdKeyword: row.id,
                payrollIdKeyword: row.payrollId,
                firstNameText: row.firstName,
                lastNameText: row.lastName,
                emailText: demoEmail(row),
                participationStatusKeyword: 'participating',
                createdAtDate: row.createdAt,
                jurisdictionsKeyword: row.jurisdictions ?? [],
                employeeIsActiveBoolean: true,
                employeeIsEnrolledBoolean: row.isEnrolled,
                employeeIsRegisteredBoolean: row.isRegistered,
              },
            },
          ],
        },
      },
    },
  };
}

/** The structural employee catch-all with the row's identity overlaid. */
function directoryEmployeeDoc(row: DirectoryRow, selection: DemoSessionSelectionSnapshot) {
  const doc = buildDemoEmployeeCatchAllDoc(selection);
  const employee = doc.data as JsonApiResource;

  return {
    ...doc,
    data: {
      ...employee,
      id: row.id,
      attributes: {
        ...employee.attributes,
        payrollId: row.payrollId,
        firstName: row.firstName,
        lastName: row.lastName,
        email: demoEmail(row),
        eligibilityStartDate: row.eligibilityStartDate,
        createdAt: row.createdAt,
        statistics: { user: { total_accounts: row.accounts, total_loans: row.loans } },
      },
    },
  };
}

function directoryRows(identity: DemoFixtureIdentity): DirectoryRow[] {
  return [
    { ...DIRECTORY_ROWS[0]!, firstName: identity.firstName, lastName: identity.lastName },
    ...COLLEAGUE_ROWS,
  ];
}

/** Named-filters aggregation: buckets are an object map keyed by bucket name. */
const namedFilters = (buckets: Record<string, number>) => ({
  scoped: {
    values: {
      buckets: Object.fromEntries(
        Object.entries(buckets).map(([key, docCount]) => [key, { docCount }])
      ),
    },
  },
});

/** Terms aggregation: buckets are an array of { key, docCount }. */
const termBuckets = (buckets: Array<{ key: string; docCount: number }>) => ({
  scoped: { values: { buckets } },
});

function buildPeopleDirectoryResponse(identity: DemoFixtureIdentity) {
  const rows = directoryRows(identity);
  const hits = rows.map(peopleHit);

  // Derived from the same row flags, so everEnrolled cannot fall below activeEnrolled.
  const activeEnrolled = rows.filter((row) => row.isRegistered).length;
  const everEnrolled = rows.filter((row) => row.isEnrolled).length;

  const roleCounts = new Map<string, number>();
  for (const role of rows.flatMap((row) => row.jurisdictions ?? [])) {
    roleCounts.set(role, (roleCounts.get(role) ?? 0) + 1);
  }

  return {
    took: 0,
    timedOut: false,
    hits: {
      total: { value: hits.length, relation: 'eq' },
      maxScore: 1,
      hits,
    },
    aggregations: {
      facetStatus: namedFilters({ active: rows.length, inactive: 0 }),
      facetParticipation: namedFilters({
        participating: rows.length,
        pending: 0,
        deactivated: 0,
      }),
      facetEnrollment: namedFilters({
        activeEnrolled,
        everEnrolled,
        neverEnrolled: rows.length - everEnrolled,
      }),
      facetRoles: termBuckets([...roleCounts].map(([key, docCount]) => ({ key, docCount }))),
      // Absent, the page shows its "no exact matches" banner on every search.
      exactNameMatches: namedFilters({ exact: rows.length }),
    },
  };
}

const COMPLETED_RUN_COUNTS = {
  created: 14,
  updated: 162,
  unchanged: 1281,
  deactivated: 6,
  reactivated: 2,
};

const ELIG_COUNT_SETS = [
  COMPLETED_RUN_COUNTS,
  { created: 9, updated: 148, unchanged: 1304, deactivated: 3, reactivated: 1 },
  { created: 21, updated: 175, unchanged: 1252, deactivated: 11, reactivated: 0 },
];

function eligibilityLogResource(
  id: string,
  fileName: string,
  status: string,
  strategy: string,
  startTime: string,
  endTime: string | null,
  counts: (typeof ELIG_COUNT_SETS)[number] | null,
  previewCounts: (typeof ELIG_COUNT_SETS)[number]
): JsonApiResource {
  return {
    type: 'eligibility-file-job-log',
    id,
    attributes: {
      fileName,
      status,
      strategy,
      startTime,
      endTime,
      createdAt: startTime,
      counts: counts
        ? { ...counts, loaded: 0, createdSentEmail: counts.created }
        : {
            created: 0,
            updated: 0,
            unchanged: 0,
            deactivated: 0,
            reactivated: 0,
            loaded: 0,
            createdSentEmail: 0,
          },
      previewCounts,
      jobErrors: [],
    },
    relationships: {
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

function buildEligibilityFilesDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const slug = employerSlug(identity.employerName);
  const logs = [
    // Newest first (the table sorts -created-at): one awaiting approval, then
    // completed monthly runs. Statuses are TERMINAL/non-polling on purpose.
    eligibilityLogResource(
      'demo-elig-log-4',
      `${slug}_eligibility_2026-06-01.csv`,
      'REQUIRES_USER_APPROVAL',
      'MANUAL',
      '2026-06-01T06:00:00Z',
      null,
      null,
      { created: 12, updated: 154, unchanged: 1296, deactivated: 4, reactivated: 1 }
    ),
    eligibilityLogResource(
      'demo-elig-log-3',
      `${slug}_eligibility_2026-05-01.csv`,
      'COMPLETED',
      'AUTOMATIC',
      '2026-05-01T06:00:00Z',
      '2026-05-01T06:11:42Z',
      ELIG_COUNT_SETS[0]!,
      ELIG_COUNT_SETS[0]!
    ),
    eligibilityLogResource(
      'demo-elig-log-2',
      `${slug}_eligibility_2026-04-01.csv`,
      'COMPLETED',
      'AUTOMATIC',
      '2026-04-01T06:00:00Z',
      '2026-04-01T06:09:17Z',
      ELIG_COUNT_SETS[1]!,
      ELIG_COUNT_SETS[1]!
    ),
    eligibilityLogResource(
      'demo-elig-log-1',
      `${slug}_eligibility_2026-03-01.csv`,
      'COMPLETED',
      'MANUAL',
      '2026-03-01T06:00:00Z',
      '2026-03-01T06:14:05Z',
      ELIG_COUNT_SETS[2]!,
      ELIG_COUNT_SETS[2]!
    ),
  ];
  return { data: logs, meta: PAGE_META(logs.length) };
}

function buildTasPaymentsDoc(): JsonApiDoc {
  const payments = [
    { id: 'demo-tas-payment-5', paymentDate: '2026-06-05', state: 'PROCESSING' },
    { id: 'demo-tas-payment-4', paymentDate: '2026-05-28', state: 'CREATED' },
    { id: 'demo-tas-payment-3', paymentDate: '2026-05-05', state: 'PAID' },
    { id: 'demo-tas-payment-2', paymentDate: '2026-04-05', state: 'PAID' },
    { id: 'demo-tas-payment-1', paymentDate: '2026-03-05', state: 'PAID' },
  ];
  return {
    data: payments.map((payment): JsonApiResource => ({
      type: 'tas-payment',
      id: payment.id,
      attributes: {
        paymentDate: payment.paymentDate,
        state: payment.state,
        paymentType: 'ACH',
        createdBy: 'Tuition.io Payments',
        description: 'Tuition assistance disbursement',
        paymentFilePaths: [],
      },
      relationships: {
        company: { data: { type: 'company', id: 'demo-tas-company-1' } },
      },
    })),
    meta: PAGE_META(payments.length),
  };
}

function reportResource(
  id: string,
  reportType: string,
  name: string,
  description: string,
  updatedAt: string
): JsonApiResource {
  return {
    type: 'report',
    id,
    attributes: {
      name,
      description,
      reportType,
      // Scope 'partner_admin' is what isPublished checks; no archivedAt.
      // Visibility is the server's answer now, so the listing does not read
      // partnerAccessConfig at all.
      visibilityLevel: 'global',
      metadata: { scope: 'partner_admin', publishedAt: '2026-05-15T12:00:00Z' },
      partnerAccessConfig: [],
      visibleToAllPartners: false,
      partnerIncludeList: [],
      partnerExcludeList: [],
      searchConfig: { viewType: 'table' },
      createdAt: '2026-02-10T12:00:00Z',
      updatedAt,
    },
  };
}

/** The saved-reports listing, served on BOTH seams (op query + URL builder). */
export function buildReportsDoc(): JsonApiDoc {
  const reports = [
    reportResource(
      'demo-report-tas',
      'tas',
      'TAS Applications by Status',
      'All tuition assistance applications grouped by approval state, refreshed daily.',
      '2026-06-02T09:30:00Z'
    ),
    reportResource(
      'demo-report-slr',
      'slr',
      'Monthly SLR Contributions',
      'Employer student-loan contributions by month, with servicer breakdown.',
      '2026-05-28T15:10:00Z'
    ),
    reportResource(
      'demo-report-pslf',
      'pslf',
      'PSLF Form Pipeline',
      'Employment certification forms by stage: drafted, employer-signed, submitted.',
      '2026-05-20T10:05:00Z'
    ),
    reportResource(
      'demo-report-core',
      'core',
      'Workforce Education Summary',
      'Cross-benefit utilization overview for the whole employee population.',
      '2026-05-12T08:45:00Z'
    ),
  ];
  return { data: reports, meta: PAGE_META(reports.length) };
}

// Detail drill-in for any eligibility row (findRecord echoes the requested
// id). A single completed-run document — the matcher vocabulary can't pick a
// per-id doc, so the pending row's detail also reads as a completed run; a
// coherent trade-off for one demo screen. Entries are linked empty (the show
// page renders an empty record list), and the riskier rich attrs (metaDiff,
// files, statusHistory) carry inert values.
function buildEligibilityLogDetailDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  return buildEligibilityJobDetailDoc({
    id: 'demo-elig-log-detail',
    fileName: `${employerSlug(identity.employerName)}_eligibility_2026-05-01.csv`,
    status: 'COMPLETED',
    strategy: 'AUTOMATIC',
    startTime: '2026-05-01T06:00:00Z',
    endTime: '2026-05-01T06:11:42Z',
  });
}

/**
 * A finished job log's detail document, with the completed run's counts, for one job.
 * `companyId` is the company the job was uploaded for; without it the job keeps the fixture log's company.
 */
export function buildEligibilityJobDetailDoc(job: {
  id: string;
  fileName: string;
  status: string;
  strategy: string;
  startTime: string;
  endTime: string;
  companyId?: string;
}): JsonApiDoc {
  const detail = eligibilityLogResource(
    job.id,
    job.fileName,
    job.status,
    job.strategy,
    job.startTime,
    job.endTime,
    COMPLETED_RUN_COUNTS,
    COMPLETED_RUN_COUNTS
  );
  detail.attributes = {
    ...detail.attributes,
    metaDiff: null,
    files: {},
    statusHistory: [],
    plans: [],
    companyMeta: [],
  };
  detail.relationships = {
    ...detail.relationships,
    ...(job.companyId ? { company: { data: { type: 'company', id: job.companyId } } } : {}),
    eligibilityFileJobLogEntries: { data: [] },
  };
  return { data: detail };
}

// Matches ONLY report detail URLs (/api/v2/reports/<id>), never the listing.
const matchesReportDetailUrl = (url: string) => /\/api\/v2\/reports\/[^/?]+/.test(url);

/**
 * A saved-report DETAIL document in RAW wire form (dasherized attribute keys,
 * single-resource `data` object) for report drill-ins. Served on BOTH seams a
 * detail read can take: the request-manager builder path (SkipCache GET) and
 * the raw-fetch fallback — viewer code reads `data.attributes`, so the
 * listing's `{ data: [] }` shape must never answer a detail URL.
 */
export function buildReportDetailRawDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  return {
    data: {
      type: 'report',
      id: 'demo-report-tas',
      attributes: {
        name: 'TAS Applications by Status',
        description: `All ${identity.employerName} tuition assistance applications grouped by approval state.`,
        'report-type': 'tas',
        'visibility-level': 'global',
        metadata: { scope: 'partner_admin', publishedAt: '2026-05-15T12:00:00Z' },
        'partner-access-config': [],
        'search-config': { viewType: 'table' },
        'created-at': '2026-02-10T12:00:00Z',
        'updated-at': '2026-06-02T09:30:00Z',
      },
    } as unknown as JsonApiResource,
  };
}

/** Keyed on the facets, not `model-type`: the palette and lookups post Person searches too. */
export function buildPeopleSearchMatcher(identity: DemoFixtureIdentity): MatcherFixture {
  return {
    matcher: {
      kind: 'builder',
      method: 'POST',
      urlMatches: matchesSearchEndpoint,
      bodyMatches: (body: unknown) => {
        const parsed = body as
          { 'model-type'?: string; 'search-body'?: { aggs?: Record<string, unknown> } } | undefined;
        return (
          parsed?.['model-type'] === 'Person' && Boolean(parsed['search-body']?.aggs?.facetStatus)
        );
      },
    },
    content: buildPeopleDirectoryResponse(identity),
  };
}

/**
 * The authored admin-table collections (+ the eligibility & report detail
 * reads). Registered by the registry AFTER product matchers (so a
 * product-specific fixture still wins) and BEFORE the remaining
 * empty-collection catch-alls.
 */
export function buildAdminTableMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    {
      matcher: { kind: 'builder', method: 'GET', urlMatches: matchesReportDetailUrl },
      content: buildReportDetailRawDoc(identity),
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'eligibility-file-job-log' },
      docs: [buildEligibilityFilesDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'eligibility-file-job-log' },
      docs: [buildEligibilityLogDetailDoc(identity)],
    },
    { matcher: { kind: 'op', op: 'query', type: 'tas-payment' }, docs: [buildTasPaymentsDoc()] },
    { matcher: { kind: 'op', op: 'query', type: 'report' }, docs: [buildReportsDoc()] },
  ];
}

/**
 * Fetch-seam twins for the admin tables whose routes read via URL builders
 * (no SkipCache — the demo handler defers them to `window.fetch`). Without
 * these the admin payments table renders "No Data" in every demo session.
 */
/**
 * Fetch-seam twins for admin tables whose routes read via URL builders (no
 * SkipCache — deferred past the demo handler). json thunks defer doc
 * construction to an actual match; the list rebuilds per intercepted request.
 */
export function buildAdminTableFetchMatchers(
  identity: DemoFixtureIdentity,
  selection: DemoSessionSelectionSnapshot
): FetchMatcher[] {
  return [
    {
      urlMatches: (url: string) => /\/tas-payments(\?|$)/.test(url),
      method: 'GET',
      json: () => toRawDoc(buildTasPaymentsDoc()),
    },
    // The saved-reports LISTING (bare collection URL) and DETAIL (single
    // resource — `data.attributes` reads) live together so their mutually
    // exclusive URL patterns cannot drift apart.
    {
      urlMatches: (url: string) => /\/api\/v2\/reports(?:\?|$)/.test(url),
      method: 'GET',
      json: () => toRawDoc(buildReportsDoc()),
    },
    {
      urlMatches: matchesReportDetailUrl,
      method: 'GET',
      json: () => buildReportDetailRawDoc(identity),
    },
    // After the identity matchers, so the session employee's own read still wins.
    {
      urlMatches: (url: string) => /\/employees\/demo-dir-employee-[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/employees\/(demo-dir-employee-[^/?]+)/.exec(url)?.[1];
        const row = directoryRows(identity).find((candidate) => candidate.id === id);
        return row ? toRawDoc(directoryEmployeeDoc(row, selection)) : { data: null };
      },
    },
  ];
}
